/**
 * callMentions: the weekly call's words and looks, shared by the board,
 * its dialog and the hospice and deaths band.
 *
 * The three tags are exactly On hospice, Passed away and Other. On hospice
 * and Passed away set the client record with a date (the server writes it
 * through the record's own status and date-of-death writes); Other writes
 * nothing on the record. Only a seat that sets a client's status
 * (manage_client_identity) may choose the two record-writing tags.
 */

import { rowDate } from './proseDate';
import type { ClientCallMentionTag } from './capacityApi';

export const MENTION_TAGS: readonly ClientCallMentionTag[] = ['hospice', 'passed_away', 'other'];

export const MENTION_TAG_LABEL: Record<ClientCallMentionTag, string> = {
  hospice: 'On hospice',
  passed_away: 'Passed away',
  other: 'Other',
};

/** The date each record-writing tag asks for. */
export const MENTION_DATE_LABEL: Record<'hospice' | 'passed_away', string> = {
  hospice: 'Since',
  passed_away: 'Date of death',
};

/** Gold for On hospice, ink for Passed away, no colour for Other. */
export const MENTION_TAG_LOOK: Record<ClientCallMentionTag, string> = {
  hospice: 'bg-warm-100 text-warm-700',
  passed_away: 'bg-ink text-cream',
  other: 'bg-sand text-ink-soft',
};

/** The line a seat that cannot set a client's status reads under the
 *  greyed tags. The seats that set it are supervising care managers and
 *  the office (manage_client_identity: owner, supervising_cm,
 *  admin_support, data_steward, exec_support). */
export const STATUS_SEATS_SET_THIS = 'Supervisors and the office set this.';

export function tagSetsRecord(tag: ClientCallMentionTag): tag is 'hospice' | 'passed_away' {
  return tag === 'hospice' || tag === 'passed_away';
}

/** A person's initials: the first letter of the first and last words. */
export function initialsOf(name: string | null): string {
  if (name === null) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = words[0]![0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]![0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/** A band chip's date: 'Oct 12', with the year when it is not this year's. */
export function bandDate(dateIso: string, todayIso: string): string {
  return rowDate(dateIso, dateIso.slice(0, 4) !== todayIso.slice(0, 4));
}

/** The note block on the board: four lines at rest, twelve with Show all.
 *  Each line is 1.5rem (leading-6). */
export const NOTE_LINES_AT_REST = 4;
export const NOTE_LINES_EXPANDED = 12;

/** The most mention chips a row shows before "+N more" (two lines of the
 *  Notes column at its narrowest; the browser shows fewer when they do
 *  not fit). */
export const ROW_MENTION_CHIPS_MAX = 6;

/** The most chips a band block shows before "+N more": eight on a wide
 *  screen, three on a phone, where each chip takes a line. */
export const BAND_CHIPS_MAX = 8;
export const BAND_CHIPS_PHONE = 3;
